/*
  File: src/pages/SeriesWatchPage.jsx
  Purpose: TV show / series watch page that starts live and shows incoming IVS metadata live in the bottom ticker.
  Behavior:
  - Auto-start (or connect to) the 'series' stream
  - Do not run player search enrichment; render metadata as-is
  - Supports pausing live ticker via the IVSPlayer favicon toggle
*/
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import IVSPlayer from '../components/Stream/IVSPlayer';
import StreamingControls from '../components/Stream/StreamingControls';
import { StreamingProvider, useStreaming, STREAMING_STATES } from '../contexts/StreamingContext';
import StopStreamModal from '../components/Stream/StopStreamModal';

function SeriesWatchContent() {
  const navigate = useNavigate();
  const [overlayItem, setOverlayItem] = useState(null);
  const [history, setHistory] = useState([]);
  const offlineStartTimeRef = useRef(null);
  const offlineTimeoutRef = useRef(null);
  const statusCheckIntervalRef = useRef(null);
  const [showStopModal, setShowStopModal] = useState(false);

  const { playbackUrl, isLoading, streamingState, error, startStream, waitAndConnect, streamType, setStreamType, streamSessionId, streamingService, stopStream, clearStreamState, checkExistingStream } = useStreaming();
  
  const API_BASE = (process.env.REACT_APP_API_BASE || '').trim().replace(/^@+/, '') || '/dev';

  useEffect(() => {
    // Connect to an already-started series stream; do not try to start here.
    setStreamType('series');
    (async () => {
      try {
        const hasExisting = await checkExistingStream('series');
        if (!hasExisting) {
          await waitAndConnect(60000, 1500, 'series');
        }
      } catch (_) {
        // swallow; UI will reflect via loading / error state
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to reset all series status to 'Not Started'
  const resetAllSeriesStatus = useCallback(async () => {
    try {
      // Fetch all series
      const res = await fetch(`${API_BASE}/series`, { headers: { accept: 'application/json' } });
      if (res.ok) {
        const data = await res.json();
        const items = Array.isArray(data?.items) ? data.items : [];
        
        // Find any live series and stop them
        const liveSeriesIds = items.filter(s => String(s.status || '').toLowerCase() === 'live').map(s => s.id);
        
        for (const seriesId of liveSeriesIds) {
          try {
            await fetch(`${API_BASE}/series/live/stop`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: seriesId })
            });
          } catch (e) {
            console.warn(`Failed to stop series ${seriesId}:`, e);
          }
        }
        
        console.log('All series status reset to Not Started');
      }
    } catch (e) {
      console.warn('Failed to reset series status:', e);
    }
  }, [API_BASE]);

  // Function to check stream status
  const checkStreamStatus = useCallback(async () => {
    try {
      const status = await streamingService.getStatus('series');
      const isLive = status?.ivs_state === 'LIVE' || String(status?.status || '').toLowerCase() === 'live';
      
      if (!isLive) {
        // Stream is offline
        if (!offlineStartTimeRef.current) {
          offlineStartTimeRef.current = Date.now();
          console.log('Stream went offline, starting 3-second countdown');
        } else {
          // Check if 3 seconds have passed
          const offlineDuration = Date.now() - offlineStartTimeRef.current;
          if (offlineDuration >= 3000) {
            console.log('Stream offline for 3+ seconds, resetting series and redirecting');
            await resetAllSeriesStatus();
            navigate('/tv');
            return;
          }
        }
      } else {
        // Stream is live, reset offline tracking
        if (offlineStartTimeRef.current) {
          console.log('Stream came back online');
          offlineStartTimeRef.current = null;
        }
      }
    } catch (e) {
      console.warn('Failed to check stream status:', e);
      // On error, treat as offline
      if (!offlineStartTimeRef.current) {
        offlineStartTimeRef.current = Date.now();
      }
    }
  }, [streamingService, resetAllSeriesStatus, navigate]);

  // Start status monitoring when component mounts
  useEffect(() => {
    const intervalId = setInterval(checkStreamStatus, 2000); // Check every 2 seconds
    statusCheckIntervalRef.current = intervalId;
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (offlineTimeoutRef.current) {
        clearTimeout(offlineTimeoutRef.current);
      }
    };
  }, [checkStreamStatus]);

  // Monitor for stream changes when navigating back to /series/watch
  useEffect(() => {
    const handleStreamChange = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const status = await streamingService.getStatus('series');
        const currentSessionId = status?.stream_session_id;
        if (currentSessionId && currentSessionId !== streamSessionId && status?.ivs_state === 'LIVE') {
          // Detected a new live session while on series watch; reconnect to refresh playback URL
          await checkExistingStream('series');
        }
      } catch (_) {
        // ignore
      }
    };

    document.addEventListener('visibilitychange', handleStreamChange);
    const intervalId = setInterval(async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const status = await streamingService.getStatus('series');
        const currentSessionId = status?.stream_session_id;
        if (currentSessionId && currentSessionId !== streamSessionId && status?.ivs_state === 'LIVE') {
          await checkExistingStream('series');
        }
      } catch (_) {}
    }, 5000);

    return () => {
      document.removeEventListener('visibilitychange', handleStreamChange);
      clearInterval(intervalId);
    };
  }, [streamingService, streamSessionId, checkExistingStream]);

  const handleMetadata = useCallback((data) => {
    const meta = data || {};
    const recvMs = (meta && meta.tim && typeof meta.tim.clt_recv_ms === 'number') ? meta.tim.clt_recv_ms : Date.now();
    const shownAt = Date.now();
    const finalMeta = {
      ...meta,
      tim: {
        ...(meta?.tim || {}),
        clt_recv_ms: recvMs,
        shown: shownAt,
        shown_ms: shownAt,
      },
    };
    // For series mode, pass raw metadata directly to the overlay
    // Let SeriesBottomMetadataOverlay handle label processing
    const toOverlay = finalMeta;
    setOverlayItem(toOverlay);
    setHistory((prev) => [{ ...finalMeta, id: shownAt }, ...prev].slice(0, 200));

    // Persist detection for series as well (same as /watch but without search timings) - skip if detection history disabled
    try {
      const isDetectionHistoryDisabled = process.env.REACT_APP_DISABLE_DETECTION_HISTORY === 'true';
      if (streamSessionId && streamingService?.postDetection && !isDetectionHistoryDisabled) {
        streamingService.postDetection(streamSessionId, finalMeta).catch(() => {});
      }
    } catch (_) {}
  }, [streamSessionId, streamingService]);

  const handleLiveToggle = useCallback((enabled) => {
    if (!enabled) {
      setOverlayItem(null);
      setHistory([]);
    }
  }, []);

  // Handle stop modal and navigation
  useEffect(() => {
    if (streamingState === STREAMING_STATES.STOPPING) {
      setShowStopModal(true);
    } else if (streamingState === STREAMING_STATES.IDLE && showStopModal) {
      // Stream stopped successfully, wait briefly then redirect
      setTimeout(() => {
        navigate('/tv');
      }, 1500);
    }
  }, [streamingState, showStopModal, navigate]);

  // Custom stop handler that shows modal
  const handleStopStream = useCallback(async () => {
    await stopStream(() => {
      setShowStopModal(true);
    });
  }, [stopStream]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
      }
      if (offlineTimeoutRef.current) {
        clearTimeout(offlineTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col pt-14 sm:pt-16">
      <Navbar />
      <div className="flex-1">
        {/* Show series mode and stream ID only in dev mode */}
        {process.env.REACT_APP_SHOW_DEV_PAGES === 'true' && (
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2">
            <div className="flex items-center gap-4 text-[11px] text-white/60">
              <span>Series Mode</span>
              <span>|</span>
              <span>Stream ID: <span className="text-white font-mono">{streamSessionId || '—'}</span></span>
            </div>
          </div>
        )}
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-6 sm:pb-10">
          <div className="rounded-xl lg:rounded-2xl overflow-hidden card relative">
            {/* Loading overlay */}
            {isLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-backgroundDark/80">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin inline-block w-8 h-8 sm:w-10 sm:h-10 border-4 border-white/30 border-t-white rounded-full" />
                  <div className="text-xs sm:text-sm text-white/80">
                    {streamingState === 'starting' ? 'Starting stream...' : 'Loading...'}
                  </div>
                  {error && <div className="text-xs text-red-300 mt-1">{error}</div>}
                </div>
              </div>
            )}

            {/* Video Player */}
            {playbackUrl ? (
              <IVSPlayer
                playbackUrl={playbackUrl}
                onMetadata={handleMetadata}
                onLiveToggle={handleLiveToggle}
                overlayMetadata={overlayItem}
                isSeriesMode={true}
                sidebarVisible={false}
              />
            ) : (
              <div className="w-full aspect-video flex items-center justify-center bg-backgroundDark/80">
                <div className="text-center px-4">
                  <div className="text-gray-300 mb-2 text-sm sm:text-base">Preparing your live series…</div>
                  <div className="text-xs sm:text-sm text-gray-500">Connecting to the live stream. This can take a few seconds…</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Controls / Health */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-2xl">
          <StreamingControls onStop={handleStopStream} />
        </div>
      </div>
      
      {/* Stop Stream Modal */}
      <StopStreamModal
        isVisible={showStopModal}
        isProcessing={streamingState === STREAMING_STATES.STOPPING}
        streamType="series"
        onComplete={() => setShowStopModal(false)}
      />
    </div>
  );
}

export default function SeriesWatchPage() {
  const rawBase = (process.env.REACT_APP_API_BASE || '').trim();
  const cleanedBase = rawBase.replace(/^@+/, '');
  const API_BASE = cleanedBase || '/dev';
  const streamingBaseUrl = `${API_BASE}/streaming`;
  return (
    <StreamingProvider baseUrl={streamingBaseUrl} defaultType="series">
      <SeriesWatchContent />
    </StreamingProvider>
  );
}
